import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, BarChart3, FileSearch, ScrollText, Lock, Loader2, ArrowRight } from "lucide-react";
import { ConsultationForm } from "@/pages/dashboard/ConsultationForm";

const sid = () => {
  let s = localStorage.getItem("rp_bsid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("rp_bsid", s); }
  return s;
};
const track = (event, meta = {}) => api.post("/public/baseline/track", { event, session_id: sid(), ...meta }).catch(() => {});

const INCLUDES = [
  "Review of up to three marketing agencies, advertising vendors, or lead providers",
  "Review of up to 6 months of available marketing expenditures",
  "Comparison of vendor-reported performance with the firm's internal results",
  "Marketing invoice and spending analysis",
  "Review of reported leads, calls, inquiries, or conversions when data is available",
  "Cost-per-qualified-conversation analysis when sufficient data is available",
  "Cost-per-retained-matter analysis when sufficient data is available",
  "Vendor accountability scorecard",
  "Identification of reporting gaps, inconsistencies, or unexplained expenses",
  "Executive findings report",
  "Private findings presentation and consultation",
];
const AUDIENCE = [
  "Work with multiple marketing agencies, advertising vendors, or lead providers",
  "Spend consistently on digital advertising or legal marketing",
  "Receive marketing reports but cannot independently verify the outcomes",
  "Want to understand which vendors contribute to qualified conversations and retained matters",
  "Need a clearer connection between marketing spending and revenue",
  "Have concerns about lead quality or vendor performance",
  "Suspect they may be paying for duplicate, unnecessary, or underperforming services",
  "Are preparing to renew, replace, or renegotiate a marketing agreement",
  "Want an independent snapshot before committing to an ongoing RetainProof subscription",
];
const STEPS = [
  ["01", "Reserve Your Review", "The firm completes the short qualification form, submits payment, and reserves an available production window."],
  ["02", "Complete the Confidential Intake", "The firm provides available marketing invoices, vendor reports, lead information, intake data, retained-matter information, and other relevant documentation."],
  ["03", "RetainProof Conducts the Analysis", "RetainProof organizes and compares marketing spending, vendor-reported activity, qualified conversations, retained matters, and available revenue information."],
  ["04", "Receive Your Findings", "The firm receives an executive findings report, vendor accountability scorecard, and private findings presentation."],
];
const EMPTY = { firm_name: "", contact_name: "", job_title: "", email: "", phone: "", website: "", practice_areas: "", monthly_spend: "", vendor_count: "", marketing_types: "", primary_concern: "", tracks_by_source: "", preferred_date: "", heard_about: "", ack_start: false, ack_scope: false };

export default function BaselineReview() {
  const [settings, setSettings] = useState(null);
  const [qualify, setQualify] = useState(false);
  const [consult, setConsult] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [prio, setPrio] = useState({ name: "", firm_name: "", email: "", phone: "", monthly_spend: "" });

  useEffect(() => {
    document.title = "RetainProof Marketing Accountability Baseline Review";
    api.get("/public/baseline/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ intro_price: 750, regular_value: 1500, availability_text: "Available for a limited time to a limited number of new law-firm clients.", reservations_open: true }));
    track("landing_visit", { source: document.referrer || "direct" });
  }, []);

  const price = settings?.intro_price ?? 750;
  const reg = settings?.regular_value ?? 1500;
  const open = settings?.reservations_open ?? true;
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitQualify = async (e) => {
    e.preventDefault();
    if (!form.ack_start || !form.ack_scope) { toast.error("Please confirm both acknowledgments."); return; }
    setBusy(true);
    track("qualification_submit");
    try {
      const q = await api.post("/public/baseline/qualify", form);
      const c = await api.post("/public/baseline/checkout", { reservation_id: q.data.reservation_id, origin_url: window.location.origin });
      track("payment_page_visit");
      window.location.href = c.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setBusy(false);
    }
  };

  const submitPriority = async (e) => {
    e.preventDefault();
    try { await api.post("/public/baseline/priority", prio); track("priority_submit"); toast.success("You're on the priority list. We'll be in touch."); setPrio({ name: "", firm_name: "", email: "", phone: "", monthly_spend: "" }); }
    catch { toast.error("Could not submit. Please try again."); }
  };

  const openReserve = () => { track("reserve_click"); setQualify(true); };
  const Offer = () => (
    <div className="inline-flex flex-col gap-1 border border-[#D5A44A]/40 bg-[#D5A44A]/5 px-5 py-4" data-testid="offer-block">
      <span className="font-display text-3xl text-[#D5A44A]">Introductory Investment: ${price.toLocaleString()}</span>
      <span className="text-sm text-zinc-400">Regular Value: <span className="line-through">${reg.toLocaleString()}</span></span>
      <span className="text-xs text-zinc-500 mt-1">{settings?.availability_text || "Available for a limited time to a limited number of new law-firm clients."}</span>
    </div>
  );

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body">
      <div className="grain-overlay" />
      {/* Minimal header — logo only, no nav */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="baseline-logo">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">Independent Marketing Accountability</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Can Your Law Firm Independently Verify Its Marketing Performance?
          </h1>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-xl">
            Agency reports may show leads, clicks, calls, impressions, and conversions—but those numbers do not always reveal which marketing investments are producing qualified conversations and retained matters. The RetainProof Marketing Accountability Baseline Review provides an independent analysis of what your firm spends, what its vendors report, and what the firm actually retains.
          </p>
          <div className="mt-8"><Offer /></div>
          <div className="mt-8 flex flex-wrap gap-4">
            {open ? (
              <Button onClick={openReserve} data-testid="hero-reserve" className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
                Reserve My Baseline Review <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            ) : (
              <a href="#priority" className="inline-flex items-center bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium">Join the Priority List</a>
            )}
            <Button onClick={() => { track("consultation_click"); setConsult(true); }} variant="outline" data-testid="hero-consultation" className="rounded-none h-12 px-8 text-base border-white/25 bg-transparent text-white hover:bg-white/5">
              Request a Confidential Consultation
            </Button>
          </div>
        </div>
        <div className="border border-white/10 bg-[#0B1F33] p-8">
          <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-6"><BarChart3 className="h-4 w-4 text-[#0E7C7B]" /> Performance intelligence</div>
          <div className="space-y-4">
            {[["Vendor-reported activity", "78%"], ["Independently verified", "41%"], ["Retained-matter attribution", "63%"]].map(([l, w]) => (
              <div key={l}>
                <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>{l}</span><span>{w}</span></div>
                <div className="h-1.5 bg-white/10"><div className="h-1.5 bg-[#D5A44A]" style={{ width: w }} /></div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-zinc-500 leading-relaxed">Illustrative. Your findings are built from your firm's own spend, vendor reports, and retained-matter data.</p>
        </div>
      </section>

      {/* Value prop */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl sm:text-4xl font-light">Marketing Reports Do Not Always Tell the Whole Story</h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">Most law firms receive performance reports directly from the same agencies, advertising platforms, or lead providers responsible for producing the results.</p>
          <p className="mt-4 text-zinc-400 leading-relaxed">RetainProof gives firms an independent way to compare vendor-reported activity with actual marketing expenditures, qualified conversations, retained matters, and available revenue information.</p>
          <p className="mt-4 text-zinc-400 leading-relaxed">The objective is not to replace the firm's marketing partners. It is to help the firm understand whether the reported activity and financial outcomes align.</p>
        </div>
      </section>

      {/* What the review includes */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-20">
        <div className="flex items-center gap-2 mb-8"><FileSearch className="h-5 w-5 text-[#D5A44A]" /><h2 className="font-display text-3xl font-light">What the Review Includes</h2></div>
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
          {INCLUDES.map((it) => (<li key={it} className="flex gap-3 text-sm text-zinc-300"><ShieldCheck className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />{it}</li>))}
        </ul>
        <p className="mt-8 text-xs text-zinc-500 border-l-2 border-[#0E7C7B] pl-3 max-w-2xl">The accuracy and depth of the analysis depend on the completeness, consistency, and availability of the information provided by the firm.</p>
      </section>

      {/* Who it's for */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl font-light mb-8">This Review Is Designed for Law Firms That:</h2>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {AUDIENCE.map((it) => (<li key={it} className="flex gap-3 text-sm text-zinc-300"><span className="text-[#D5A44A] mt-0.5">—</span>{it}</li>))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <h2 className="font-display text-3xl font-light mb-10">How the Process Works</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {STEPS.map(([n, t, d]) => (
            <div key={n} className="border border-white/10 bg-[#0B1F33] p-6">
              <span className="font-mono-data text-[#D5A44A] text-sm">{n}</span>
              <h3 className="font-display text-lg mt-3 leading-tight">{t}</h3>
              <p className="text-xs text-zinc-400 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 border-t border-white/10 bg-[#12324D]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-light">Stop Relying on Marketing Reports Alone</h2>
          <p className="mt-5 text-zinc-300 max-w-2xl mx-auto leading-relaxed">See whether your agencies, advertising vendors, and lead providers are producing outcomes that support your firm's growth.</p>
          <div className="mt-8 flex justify-center"><Offer /></div>
          <div className="mt-8">
            {open ? (
              <Button onClick={openReserve} data-testid="final-reserve" className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-10 text-base font-medium">Reserve Your Baseline Review</Button>
            ) : (
              <a href="#priority" className="inline-flex items-center bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-10 text-base font-medium">Join the Priority List</a>
            )}
            <p className="mt-4 text-xs text-zinc-500">Secure payment. Confidential intake. Limited introductory availability.</p>
          </div>
        </div>
      </section>

      {/* Priority list (shown when paused) */}
      {!open && (
        <section id="priority" className="relative z-10 max-w-xl mx-auto px-6 py-16">
          <div className="border border-white/10 bg-[#0B1F33] p-8">
            <p className="text-sm text-zinc-300">{settings?.paused_message}</p>
            <form onSubmit={submitPriority} className="mt-6 space-y-3" data-testid="priority-form">
              <Input required placeholder="Name" value={prio.name} onChange={(e) => setPrio({ ...prio, name: e.target.value })} className="rounded-none bg-white/5 border-white/15 h-11" />
              <Input required placeholder="Firm name" value={prio.firm_name} onChange={(e) => setPrio({ ...prio, firm_name: e.target.value })} className="rounded-none bg-white/5 border-white/15 h-11" />
              <Input required type="email" placeholder="Email" value={prio.email} onChange={(e) => setPrio({ ...prio, email: e.target.value })} className="rounded-none bg-white/5 border-white/15 h-11" />
              <Input placeholder="Telephone" value={prio.phone} onChange={(e) => setPrio({ ...prio, phone: e.target.value })} className="rounded-none bg-white/5 border-white/15 h-11" />
              <Input placeholder="Approx. monthly marketing spend" value={prio.monthly_spend} onChange={(e) => setPrio({ ...prio, monthly_spend: e.target.value })} className="rounded-none bg-white/5 border-white/15 h-11" />
              <Button type="submit" className="w-full rounded-none bg-[#D5A44A] text-black hover:bg-[#B8923D] h-11" data-testid="priority-submit">Join the priority list</Button>
            </form>
          </div>
        </section>
      )}

      {/* Disclaimers + footer */}
      <footer className="relative z-10 border-t border-white/10 bg-[#0A1B2E]">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="text-[11px] text-zinc-500 leading-relaxed space-y-1.5 max-w-3xl">
            <p>Payment reserves the firm's production window. Reviews begin after the required information has been received. Delivery timing will be confirmed after the intake is reviewed.</p>
            <p>RetainProof does not provide legal, accounting, tax, investment, or financial advice. RetainProof does not guarantee savings, revenue, case volume, vendor refunds, or specific financial results, and does not guarantee that every requested calculation can be completed when sufficient data is unavailable.</p>
            <p>All firm information is handled confidentially and used solely for the authorized review and related RetainProof services. The firm is responsible for ensuring that it is authorized to provide all submitted information.</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-zinc-400">
            <Link to="/" className="hover:text-white" data-testid="footer-home">RetainProof homepage</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms and Conditions</Link>
            <a href="mailto:clientcare@retainproofnow.com" className="hover:text-white">Contact</a>
          </div>
          <p className="mt-6 font-mono-data text-[10px] text-zinc-600">© 2026 RetainProof™ · Independent Law-Firm Marketing Accountability and Performance Intelligence</p>
        </div>
      </footer>

      {/* Qualification dialog */}
      <Dialog open={qualify} onOpenChange={setQualify}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qualify-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reserve Your Baseline Review</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 -mt-1">Tell us about your firm. Eligible firms proceed to secure payment of ${price.toLocaleString()}.</p>
          <form onSubmit={submitQualify} className="grid sm:grid-cols-2 gap-3 mt-2">
            <F l="Firm name" req><Input required value={form.firm_name} onChange={upd("firm_name")} className="rounded-none" data-testid="q-firm" /></F>
            <F l="Contact name" req><Input required value={form.contact_name} onChange={upd("contact_name")} className="rounded-none" data-testid="q-contact" /></F>
            <F l="Job title"><Input value={form.job_title} onChange={upd("job_title")} className="rounded-none" /></F>
            <F l="Email" req><Input required type="email" value={form.email} onChange={upd("email")} className="rounded-none" data-testid="q-email" /></F>
            <F l="Telephone"><Input value={form.phone} onChange={upd("phone")} className="rounded-none" /></F>
            <F l="Firm website"><Input value={form.website} onChange={upd("website")} className="rounded-none" /></F>
            <F l="Primary practice areas"><Input value={form.practice_areas} onChange={upd("practice_areas")} className="rounded-none" /></F>
            <F l="Approx. monthly marketing spend"><Input value={form.monthly_spend} onChange={upd("monthly_spend")} className="rounded-none" /></F>
            <F l="Number of current marketing vendors"><Input value={form.vendor_count} onChange={upd("vendor_count")} className="rounded-none" data-testid="q-vendors" /></F>
            <F l="Types of marketing currently used"><Input value={form.marketing_types} onChange={upd("marketing_types")} className="rounded-none" /></F>
            <F l="Tracks retained matters by source?"><Input value={form.tracks_by_source} onChange={upd("tracks_by_source")} placeholder="Yes / No / Unsure" className="rounded-none" /></F>
            <F l="Preferred consultation / intake date"><Input type="date" value={form.preferred_date} onChange={upd("preferred_date")} className="rounded-none" /></F>
            <F l="How did you hear about RetainProof?"><Input value={form.heard_about} onChange={upd("heard_about")} className="rounded-none" /></F>
            <div className="sm:col-span-2"><F l="Primary concern about marketing performance"><Textarea rows={2} value={form.primary_concern} onChange={upd("primary_concern")} className="rounded-none" /></F></div>
            <label className="sm:col-span-2 flex gap-2 text-xs text-zinc-600"><input type="checkbox" checked={form.ack_start} onChange={(e) => setForm({ ...form, ack_start: e.target.checked })} data-testid="q-ack-start" className="mt-0.5" />I understand that the RetainProof Baseline Review begins after the requested information and documentation have been received and confirmed.</label>
            <label className="sm:col-span-2 flex gap-2 text-xs text-zinc-600"><input type="checkbox" checked={form.ack_scope} onChange={(e) => setForm({ ...form, ack_scope: e.target.checked })} data-testid="q-ack-scope" className="mt-0.5" />I understand that RetainProof provides marketing performance analysis and does not provide legal, accounting, tax, or financial advice.</label>
            <Button type="submit" disabled={busy} className="sm:col-span-2 w-full rounded-none bg-[#D5A44A] text-black hover:bg-[#B8923D] h-11" data-testid="q-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Continue to secure payment — ${price.toLocaleString()}
            </Button>
            <p className="sm:col-span-2 flex items-center gap-1.5 text-[11px] text-zinc-500"><Lock className="h-3 w-3" />Payment reserves your production window. Reviews begin after documentation is received and confirmed.</p>
          </form>
        </DialogContent>
      </Dialog>

      <ConsultationForm open={consult} onClose={() => setConsult(false)} requestType="baseline_review" preferredPlan="RetainProof Baseline Marketing Assessment" title="Request a Confidential Consultation" />
    </div>
  );
}

function F({ l, req, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">{l}{req ? " *" : ""}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
